import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { SideDrawerComponent } from "../shell/side-drawer.component";
import {
  CREW_CLEATS,
  CrewCommandsStore,
  LINE_COMMANDS,
  LINE_LABELS,
  type LineState,
} from "../core/sim/crew-commands.store";

/**
 * Position grid entry: 6 visual slots arranged as
 * Port (LB) row × {Rufa, Środek, Dziób} cols, then Starboard (PB).
 * Both bow slots map to the same centerline `bow` cleat — the only
 * cleat on the foredeck — so the player can reach it from either
 * side of the boat without having to remember it's centerline.
 */
interface PositionSlot {
  readonly side: "port" | "starboard";
  readonly column: "stern" | "mid" | "bow";
  readonly cleat: string;
  readonly label: string;
}

const POSITIONS: readonly PositionSlot[] = [
  { side: "port", column: "stern", cleat: "stern_port", label: "Rufa" },
  { side: "port", column: "mid", cleat: "midship_port", label: "Środek" },
  { side: "port", column: "bow", cleat: "bow", label: "Dziób" },
  {
    side: "starboard",
    column: "stern",
    cleat: "stern_starboard",
    label: "Rufa",
  },
  {
    side: "starboard",
    column: "mid",
    cleat: "midship_starboard",
    label: "Środek",
  },
  { side: "starboard", column: "bow", cleat: "bow", label: "Dziób" },
];

/**
 * Mooring drawer — two-step UX:
 *  1. Pick a position on the boat from a 2×3 grid (Port / Starboard
 *     × Rufa / Środek / Dziób).
 *  2. See and issue commands for the cleat at that position
 *     (Przygotuj / Rzuć / Wybierz / Obłóż / Luzuj / Zwiń).
 *
 * The shore-party controls live above the grid because they affect
 * every line. Each command takes 2–5 s on the underlying store; the
 * row is shown as busy and buttons disabled while a transition runs.
 */
@Component({
  selector: "po-mooring-drawer",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SideDrawerComponent],
  templateUrl: "./mooring-drawer.component.html",
  styleUrl: "./mooring-drawer.component.css",
})
export class MooringDrawerComponent {
  readonly open = input.required<boolean>();
  readonly anchorBottom = input<number>(64);
  readonly closeRequest = output<void>();

  readonly store = inject(CrewCommandsStore);
  readonly positions = POSITIONS;
  readonly commands = LINE_COMMANDS;

  /** Currently inspected cleat key, or null when only the grid is visible. */
  readonly selected = signal<string | null>(null);

  readonly view = computed(() => this.store.lines());
  readonly shore = computed(() => this.store.shore());
  readonly crewAshore = computed(() => this.store.crewAshore());

  constructor() {
    // Reset the selection whenever the drawer is closed so reopening
    // the panel always lands on the location grid first.
    effect(() => {
      if (!this.open()) this.selected.set(null);
    });
  }

  select(cleat: string): void {
    this.selected.update((cur) => (cur === cleat ? null : cleat));
  }

  cleatLabel(key: string): string {
    return CREW_CLEATS.find((c) => c.key === key)?.label ?? key;
  }

  stateLabel(s: LineState | undefined): string {
    return s ? LINE_LABELS[s] : "—";
  }

  /**
   * Tooltip explaining why a button might be disabled — distinguishes
   * "no shore party" (bollard make-fast) from "no buoy in reach"
   * (boat-hook pickup).
   */
  commandHint(cleatKey: string, cmdId: string): string {
    if (cmdId === "make_fast") {
      const entry = this.store.lines()[cleatKey];
      if (entry && entry.targetKind !== "buoy" && !this.crewAshore()) {
        return "Wymaga załogi na brzegu";
      }
    }
    if (cmdId === "loop_buoy") {
      const entry = this.store.lines()[cleatKey];
      if (
        cleatKey !== "bow" &&
        cleatKey !== "stern_port" &&
        cleatKey !== "stern_starboard"
      ) {
        return "Bosakiem przełożysz cumę tylko z dziobu lub rufy";
      }
      if (!entry || entry.state !== "prepared") {
        return "Najpierw przygotuj cumę";
      }
      const cand = this.store.pickupCandidate(cleatKey);
      if (!cand) return "Brak bojki w zasięgu bosaka";
      const dist = cand.distance.toFixed(1);
      return cleatKey === "bow"
        ? `Bojka w zasięgu (${dist} m) — z dziobu trwa to dłużej`
        : `Bojka w zasięgu (${dist} m)`;
    }
    return "";
  }
}
